import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../ComingSoon/_shared/AppIcon';
import { LeaveBalanceView, LeaveRequestInput, HalfDayPart, LeaveTypeView, BlackoutCheckResult, BlackoutSeverity, PolicyValidationConfig } from '../../../../models/attendance';
import { checkBlackoutPeriods, getValidationConfig } from '../../../../api/Attendance/leaves';
import { EnforcementPreview } from '../../../../components/leaves/EnforcementPreview';

interface LeaveApplicationFormProps {
  onSubmit: (input: LeaveRequestInput, files: File[]) => Promise<void>;
  onCancel: () => void;
  balances: LeaveBalanceView[];
  leaveTypes: LeaveTypeView[];
  userId: string;
  submitting?: boolean;
  onBehalfEmployeeName?: string;
}

interface FormErrors {
  [key: string]: string;
}

const LeaveApplicationForm: React.FC<LeaveApplicationFormProps> = ({
  onSubmit,
  onCancel,
  balances,
  leaveTypes,
  userId,
  submitting = false,
  onBehalfEmployeeName,
}) => {
  const { t } = useTranslation(['leaves', 'common']);

  const [leaveTypeId, setLeaveTypeId] = useState<number>(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fromHalfDay, setFromHalfDay] = useState(false);
  const [fromHalfDayPart, setFromHalfDayPart] = useState<HalfDayPart>(HalfDayPart.AM);
  const [toHalfDay, setToHalfDay] = useState(false);
  const [toHalfDayPart, setToHalfDayPart] = useState<HalfDayPart>(HalfDayPart.PM);
  const [reason, setReason] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [blackoutCheck, setBlackoutCheck] = useState<BlackoutCheckResult | null>(null);
  const [validationConfig, setValidationConfig] = useState<PolicyValidationConfig | null>(null);
  // When the company allows leave for dates with no defined schedule, the backend counts a calendar day
  // per this weekday mask (7-bit over getDay()) for any date strictly after coverageEnd; within coverage
  // the real roster is authoritative. Mirror that in the preview (the backend stays the source of truth).
  const [scheduleInfo, setScheduleInfo] = useState<{ allow: boolean; coverageEnd: Date | null; mask: number } | null>(null);

  // Fetch policy validation config for the user
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    getValidationConfig(userId)
      .then(result => {
        if (!cancelled && result.errors.length === 0 && result.payload) {
          if (result.payload.policyId) {
            setValidationConfig(result.payload);
          }
          setScheduleInfo({
            allow: !!result.payload.allowLeaveWithoutSchedule,
            coverageEnd: result.payload.scheduleCoverageEndUtc ? new Date(result.payload.scheduleCoverageEndUtc) : null,
            mask: result.payload.leaveWithoutScheduleWorkingDays ?? 127,
          });
        }
      })
      .catch(() => { /* ignore network errors */ });
    return () => { cancelled = true; };
  }, [userId]);

  // Check blackout periods when leave type and dates change
  useEffect(() => {
    if (!leaveTypeId || !startDate || !endDate) {
      setBlackoutCheck(null);
      return;
    }
    let cancelled = false;
    checkBlackoutPeriods(leaveTypeId, startDate, endDate)
      .then(result => {
        if (!cancelled && result.errors.length === 0) {
          setBlackoutCheck(result.payload);
        }
      })
      .catch(() => { /* ignore network errors for preview */ });
    return () => { cancelled = true; };
  }, [leaveTypeId, startDate, endDate]);

  const isStrictBlackout = blackoutCheck?.isBlocked === true;

  const isSingleDay = startDate && endDate && startDate === endDate;

  const selectedLeaveType = leaveTypes.find(lt => lt.id === leaveTypeId);
  const selectedBalance = balances.find(b => b.leaveTypeId === leaveTypeId);
  const isAttachmentRequired = selectedLeaveType?.attachmentRequired === true;

  // A single-day request can only carry one half (the backend rejects a half on both
  // "from" and "to" when start == end), so collapse the "last day" half in that case.
  useEffect(() => {
    if (isSingleDay && toHalfDay) {
      setToHalfDay(false);
    }
  }, [isSingleDay, toHalfDay]);

  const calculateDuration = (): number => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    let days: number;
    if (selectedBalance?.includeNonWorkingrDays === false) {
      let count = 0;
      const d = new Date(start);
      const allow = scheduleInfo?.allow === true;
      const coverageEnd = scheduleInfo?.coverageEnd ?? null;
      const mask = scheduleInfo?.mask ?? 127;
      const dayKey = (x: Date) => x.getFullYear() * 10000 + x.getMonth() * 100 + x.getDate();
      const coverageKey = coverageEnd ? dayKey(coverageEnd) : null;
      while (d <= end) {
        const day = d.getDay();
        // A date beyond the employee's schedule coverage (no roster defined that far, or no schedule at
        // all) counts per the company's configured working weekdays; within coverage the legacy Mon–Fri
        // estimate stands. The backend is authoritative either way.
        const beyondCoverage = allow && (coverageKey === null || dayKey(d) > coverageKey);
        const counts = beyondCoverage ? ((mask & (1 << day)) !== 0) : (day !== 0 && day !== 6);
        if (counts) count++;
        d.setDate(d.getDate() + 1);
      }
      days = count;
    } else {
      const diffTime = Math.abs(end.getTime() - start.getTime());
      days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }
    // Each selected half (first and/or last day) takes 0.5 off the total. On a single-day
    // request only the "from" half applies (toHalfDay is forced off above).
    if (fromHalfDay) days -= 0.5;
    if (toHalfDay && !isSingleDay) days -= 0.5;
    return Math.max(days, 0);
  };

  const duration = calculateDuration();

  const maxConsecutiveDays = validationConfig?.maxConsecutiveDays;
  const exceedsMaxConsecutive = maxConsecutiveDays != null && duration > 0 && duration > maxConsecutiveDays;

  // Minimum notice period validation
  const minNoticeDays = validationConfig?.minNoticeDays ?? 0;
  const daysUntilStart = startDate
    ? Math.floor((new Date(startDate).getTime() - new Date(new Date().toISOString().split('T')[0]).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const insufficientNotice = minNoticeDays > 0 && daysUntilStart != null && daysUntilStart < minNoticeDays;

  // Calculate sandwich days: non-working days (Sat/Sun) between first and last working day in range
  const calculateSandwichDays = useCallback((): { count: number; dates: Date[] } => {
    // The backend skips the sandwich rule when the request reaches beyond the employee's schedule
    // coverage (mask-governed dates have no roster to sandwich), so the preview must too.
    const dayKey = (x: Date) => x.getFullYear() * 10000 + x.getMonth() * 100 + x.getDate();
    const requestBeyondCoverage = !!scheduleInfo?.allow && !!endDate
      && (scheduleInfo.coverageEnd === null || dayKey(new Date(endDate)) > dayKey(scheduleInfo.coverageEnd));
    if (requestBeyondCoverage || !validationConfig?.sandwichRuleEnabled || !startDate || !endDate) {
      return { count: 0, dates: [] };
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (totalDays <= 1) return { count: 0, dates: [] };

    // Get working status for each day (0=Sun, 6=Sat are weekends)
    const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;
    const days: { date: Date; isNonWorking: boolean }[] = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push({ date: d, isNonWorking: isWeekend(d) });
    }

    // Find first and last working day
    let firstW = -1, lastW = -1;
    for (let i = 0; i < days.length; i++) {
      if (!days[i].isNonWorking) {
        if (firstW === -1) firstW = i;
        lastW = i;
      }
    }

    if (firstW < 0 || lastW <= firstW) return { count: 0, dates: [] };

    const sandwichDates: Date[] = [];
    for (let i = firstW + 1; i < lastW; i++) {
      if (days[i].isNonWorking) {
        sandwichDates.push(days[i].date);
      }
    }
    return { count: sandwichDates.length, dates: sandwichDates };
  }, [validationConfig?.sandwichRuleEnabled, startDate, endDate, scheduleInfo?.allow, scheduleInfo?.coverageEnd]);

  const sandwichInfo = calculateSandwichDays();

  const exceedsBalance = !!selectedBalance && duration > selectedBalance.balance;
  const hasRequiredFields = leaveTypeId > 0 && !!startDate && !!endDate && reason.trim().length > 0;
  const datesOrdered = !!startDate && !!endDate && new Date(startDate) <= new Date(endDate);

  const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
  const maxSize = 5 * 1024 * 1024;

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    const allFiles = Array.from(files);
    const newErrors: FormErrors = { ...errors };
    delete newErrors.attachments;

    const unsupported = allFiles.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
      return !allowedExtensions.includes(ext);
    });
    if (unsupported.length > 0) {
      newErrors.attachments = t('leaves:unsupportedFileType', { name: unsupported[0].name, types: allowedExtensions.join(', ') });
      setErrors(newErrors);
      return;
    }

    const oversized = allFiles.filter(f => f.size > maxSize);
    if (oversized.length > 0) {
      newErrors.attachments = t('leaves:fileTooLarge', { name: oversized[0].name, maxMB: 5 });
      setErrors(newErrors);
      return;
    }

    setErrors(newErrors);
    setAttachments(prev => [...prev, ...allFiles]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!leaveTypeId) {
      newErrors.leaveType = t('Please select a leave type');
    }
    if (!startDate) {
      newErrors.startDate = t('Please select start date');
    }
    if (!endDate) {
      newErrors.endDate = t('Please select end date');
    }
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      newErrors.endDate = t('End date must be after start date');
    }
    if (!reason.trim()) {
      newErrors.reason = t('Please provide a reason');
    }

    if (selectedBalance && duration > selectedBalance.balance) {
      newErrors.duration = `${t('Insufficient balance')}. ${t('Available')}: ${selectedBalance.balance} ${t('days')}`;
    }

    if (exceedsMaxConsecutive && maxConsecutiveDays != null) {
      newErrors.maxConsecutive = t('leaves:maxConsecutiveExceeded', { max: maxConsecutiveDays, requested: duration });
    }

    if (isAttachmentRequired && attachments.length === 0) {
      newErrors.attachments = t('leaves:This leave type requires an attachment');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    const lastHalf = !isSingleDay && toHalfDay;
    const input: LeaveRequestInput = {
      leaveTypeId,
      userId,
      startDate,
      fromHalfDay,
      fromHalfDayPart: fromHalfDay ? fromHalfDayPart : HalfDayPart.AM,
      endDate,
      toHalfDay: lastHalf,
      toHalfDayPart: lastHalf ? toHalfDayPart : HalfDayPart.AM,
      comments: reason,
      hasAttachments: attachments.length > 0,
    };

    await onSubmit(input, attachments);
  };

  const renderHalfDayPeriod = (
    part: HalfDayPart,
    setPart: (value: HalfDayPart) => void,
    namePrefix: string,
  ) => (
    <div className="flex items-center space-x-4 rtl:space-x-reverse ms-7">
      <span className="text-sm font-medium text-foreground">{t('Half Day Period')}:</span>
      <label className="flex items-center space-x-1 rtl:space-x-reverse">
        <input
          type="radio"
          name={`${namePrefix}-part`}
          value={HalfDayPart.AM}
          checked={part === HalfDayPart.AM}
          onChange={() => setPart(HalfDayPart.AM)}
          data-testid={`${namePrefix}-am`}
          className="text-emerald-600 focus:ring-emerald-500"
        />
        <span className="text-sm text-foreground">{t('AM')}</span>
      </label>
      <label className="flex items-center space-x-1 rtl:space-x-reverse">
        <input
          type="radio"
          name={`${namePrefix}-part`}
          value={HalfDayPart.PM}
          checked={part === HalfDayPart.PM}
          onChange={() => setPart(HalfDayPart.PM)}
          data-testid={`${namePrefix}-pm`}
          className="text-emerald-600 focus:ring-emerald-500"
        />
        <span className="text-sm text-foreground">{t('PM')}</span>
      </label>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-foreground">{t('Apply for Leave')}</h3>
            <button
              onClick={onCancel}
              data-testid="leave-form-close"
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
          {onBehalfEmployeeName && (
            <div className="mt-2" data-testid="on-behalf-badge">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                <Icon name="UserCheck" size={14} />
                {t('leaves:onBehalfBadge', { name: onBehalfEmployeeName })}
              </span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Leave Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Leave Type')} *
            </label>
            <select
              value={leaveTypeId || ''}
              onChange={(e) => setLeaveTypeId(Number(e.target.value))}
              data-testid="leave-type-select"
              className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                errors.leaveType ? 'border-red-500' : 'border-border'
              } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
            >
              <option value="">{t('Select leave type')}</option>
              {leaveTypes.map((lt) => {
                const bal = balances.find(b => b.leaveTypeId === lt.id);
                const unavailable = !bal || bal.balance < 1;
                return (
                  <option key={lt.id} value={lt.id} disabled={unavailable}>
                    {lt.name} {bal ? `(${bal.balance} ${t('days available')})` : `(${t('no balance')})`}
                  </option>
                );
              })}
            </select>
            {errors.leaveType && (
              <p className="text-red-500 text-sm mt-1">{errors.leaveType}</p>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('Start Date')} *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="leave-start-date"
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                  errors.startDate ? 'border-red-500' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('End Date')} *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="leave-end-date"
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                  errors.endDate ? 'border-red-500' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                min={startDate || new Date().toISOString().split('T')[0]}
              />
              {errors.endDate && (
                <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Blackout Warning */}
          {blackoutCheck && blackoutCheck.severity && (
            <div
              data-testid={`blackout-warning-${blackoutCheck.severity?.toLowerCase()}`}
              role={blackoutCheck.isBlocked ? 'alert' : 'status'}
              className={`flex items-center gap-3 p-3 rounded-lg text-sm ${
                blackoutCheck.severity === BlackoutSeverity.Strict
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : blackoutCheck.severity === BlackoutSeverity.Advisory
                    ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                    : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}
            >
              <Icon
                name={blackoutCheck.isBlocked ? 'ShieldAlert' : 'AlertTriangle'}
                size={20}
                className="flex-shrink-0"
              />
              <div>
                <p className="font-medium">
                  {blackoutCheck.isBlocked
                    ? t('leaves:blackoutStrict', { name: blackoutCheck.blackoutName })
                    : blackoutCheck.severity === BlackoutSeverity.Advisory
                      ? t('leaves:blackoutAdvisory', { name: blackoutCheck.blackoutName })
                      : t('leaves:blackoutConditional', { name: blackoutCheck.blackoutName })}
                </p>
                {blackoutCheck.reason && (
                  <p className="mt-1 text-xs opacity-80">{blackoutCheck.reason}</p>
                )}
              </div>
            </div>
          )}

          {/* Notice Period Warning */}
          {insufficientNotice && daysUntilStart != null && (
            <div
              data-testid="notice-period-warning"
              role="status"
              className="flex items-center gap-3 p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-800"
            >
              <Icon name="AlertTriangle" size={20} className="flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {t('leaves:noticePeriodWarning', { required: minNoticeDays, provided: daysUntilStart })}
                </p>
                <p className="mt-1 text-xs opacity-80">
                  {t('leaves:noticePeriodHint')}
                </p>
              </div>
            </div>
          )}

          {/* Notice Period Info (within requirement) */}
          {minNoticeDays > 0 && daysUntilStart != null && !insufficientNotice && startDate && (
            <div
              data-testid="notice-period-info"
              role="status"
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <Icon name="Info" size={14} className="flex-shrink-0" />
              <span>{t('leaves:noticePeriodInfo', { provided: daysUntilStart, required: minNoticeDays })}</span>
            </div>
          )}

          {/* Max Consecutive Days Warning */}
          {exceedsMaxConsecutive && maxConsecutiveDays != null && (
            <div
              data-testid="max-consecutive-warning"
              role="alert"
              className="flex items-center gap-3 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-800"
            >
              <Icon name="ShieldAlert" size={20} className="flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {t('leaves:maxConsecutiveExceeded', { max: maxConsecutiveDays, requested: duration })}
                </p>
                <p className="mt-1 text-xs opacity-80">
                  {t('leaves:maxConsecutiveHint')}
                </p>
              </div>
            </div>
          )}

          {/* Max Consecutive Days Info (within limit) */}
          {maxConsecutiveDays != null && duration > 0 && !exceedsMaxConsecutive && (
            <div
              data-testid="max-consecutive-info"
              role="status"
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <Icon name="Info" size={14} className="flex-shrink-0" />
              <span>{t('leaves:maxConsecutiveInfo', { current: duration, max: maxConsecutiveDays })}</span>
            </div>
          )}

          {/* Sandwich Rule Info */}
          {sandwichInfo.count > 0 && (
            <div
              data-testid="sandwich-rule-info"
              role="status"
              className="flex items-center gap-3 p-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-800"
            >
              <Icon name="AlertTriangle" size={20} className="flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {t('leaves:sandwichRuleApplied', { count: sandwichInfo.count })}
                </p>
                <p className="mt-1 text-xs opacity-80">
                  {sandwichInfo.dates.map(d => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })).join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Half Day — single day: one toggle bound to the "from" half */}
          {isSingleDay && (
            <div className="space-y-3" data-testid="half-day-single">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  id="halfDay"
                  checked={fromHalfDay}
                  onChange={(e) => setFromHalfDay(e.target.checked)}
                  data-testid="leave-half-day"
                  className="rounded border-border text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="halfDay" className="text-sm text-foreground">
                  {t('Half Day Leave')}
                </label>
              </div>
              {fromHalfDay && renderHalfDayPeriod(fromHalfDayPart, setFromHalfDayPart, 'leave-half-day')}
            </div>
          )}

          {/* Half Day — multi-day: first and/or last day can be a half day */}
          {!!startDate && !!endDate && !isSingleDay && (
            <div className="space-y-3" data-testid="half-day-multi">
              <p className="text-sm font-medium text-foreground">{t('leaves:halfDayPartialLabel')}</p>

              {/* First day */}
              <div className="rounded-lg border border-border p-3 space-y-3">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <input
                    type="checkbox"
                    id="fromHalfDay"
                    checked={fromHalfDay}
                    onChange={(e) => setFromHalfDay(e.target.checked)}
                    data-testid="leave-from-half-day"
                    className="rounded border-border text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="fromHalfDay" className="text-sm text-foreground">
                    {t('leaves:firstDayHalf')}{' '}
                    <span className="text-muted-foreground">({new Date(startDate).toLocaleDateString()})</span>
                  </label>
                </div>
                {fromHalfDay && renderHalfDayPeriod(fromHalfDayPart, setFromHalfDayPart, 'leave-from-half')}
              </div>

              {/* Last day */}
              <div className="rounded-lg border border-border p-3 space-y-3">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <input
                    type="checkbox"
                    id="toHalfDay"
                    checked={toHalfDay}
                    onChange={(e) => setToHalfDay(e.target.checked)}
                    data-testid="leave-to-half-day"
                    className="rounded border-border text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="toHalfDay" className="text-sm text-foreground">
                    {t('leaves:lastDayHalf')}{' '}
                    <span className="text-muted-foreground">({new Date(endDate).toLocaleDateString()})</span>
                  </label>
                </div>
                {toHalfDay && renderHalfDayPeriod(toHalfDayPart, setToHalfDayPart, 'leave-to-half')}
              </div>
            </div>
          )}

          {/* Duration Display */}
          {duration > 0 && (
            <div className="bg-muted/30 p-4 rounded-lg" data-testid="leave-duration-display">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('Duration')}</span>
                <span className="font-medium text-foreground">
                  {duration} {duration === 1 ? t('day') : t('days')}
                </span>
              </div>
              {sandwichInfo.count > 0 && (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-amber-700">{t('leaves:sandwichDaysIncluded', { count: sandwichInfo.count })}</span>
                </div>
              )}
              {selectedBalance && (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-muted-foreground">{t('Available Balance')}</span>
                  <span className="font-medium text-foreground">
                    {selectedBalance.balance} {t('days')}
                  </span>
                </div>
              )}
            </div>
          )}

          {errors.duration && (
            <p className="text-red-500 text-sm">{errors.duration}</p>
          )}

          {/* Enforcement Preview */}
          <EnforcementPreview
            variant="card"
            visible={leaveTypeId > 0 && !!startDate && !!endDate}
            balance={selectedBalance ?? null}
            daysRequested={duration}
            sandwichDays={sandwichInfo.count}
            blackoutCheck={blackoutCheck}
            validationConfig={validationConfig}
            daysUntilStart={daysUntilStart}
            exceedsMaxConsecutive={exceedsMaxConsecutive}
            loading={false}
          />

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('Reason')} *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder={t('reasonPlaceholder')}
              data-testid="leave-reason"
              className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground ${
                errors.reason ? 'border-red-500' : 'border-border'
              } focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none`}
            />
            {errors.reason && (
              <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
            )}
          </div>

          {/* File Upload */}
          <div data-testid="leave-attachments-section">
            <label className="block text-sm font-medium text-foreground mb-2">
              {isAttachmentRequired ? `${t('Attachments')} *` : t('Attachments (Optional)')}
            </label>

            {isAttachmentRequired && attachments.length === 0 && (
              <div
                data-testid="attachment-required-warning"
                role="alert"
                className="flex items-center gap-2 p-3 mb-3 rounded-lg text-sm bg-amber-50 border border-amber-200 text-amber-800"
              >
                <Icon name="AlertTriangle" size={16} className="flex-shrink-0" />
                <span>{t('leaves:This leave type requires an attachment')}</span>
              </div>
            )}

            <div
              data-testid="leave-attachment-dropzone"
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragOver
                  ? 'border-emerald-500 bg-emerald-50'
                  : isAttachmentRequired && attachments.length === 0
                    ? 'border-amber-400 hover:border-amber-500'
                    : 'border-border hover:border-emerald-400'
              }`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
            >
              <Icon name="Upload" size={32} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                {t('dropzoneText')}
              </p>
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
                id="file-upload"
                data-testid="leave-attachment-input"
              />
              <label
                htmlFor="file-upload"
                data-testid="leave-attachment-browse"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer"
              >
                {t('Browse Files')}
              </label>
              <p className="text-xs text-muted-foreground mt-2">
                {t('supportedFormats')}
              </p>
            </div>

            {errors.attachments && (
              <p className="text-red-500 text-sm mt-1">{errors.attachments}</p>
            )}

            {attachments.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="leave-attachment-list">
                <h4 className="text-sm font-medium text-foreground">{t('Uploaded Files')}:</h4>
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      data-testid={`leave-attachment-remove-${index}`}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 rtl:space-x-reverse pt-4 border-t border-border">
            <button
              type="button"
              onClick={onCancel}
              data-testid="leave-form-cancel"
              className="px-6 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border hover:border-border/80 rounded-lg transition-colors"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || isStrictBlackout || exceedsMaxConsecutive || exceedsBalance || !hasRequiredFields || !datesOrdered || (isAttachmentRequired && attachments.length === 0)}
              data-testid="leave-submit-btn"
              className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {submitting ? t('Loading') : t('Submit Application')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveApplicationForm;
